import { IonAlert, IonButton, IonIcon, IonSkeletonText, IonSpinner } from '@ionic/react'
import { GeneInfoItem } from 'domain/usecases/get-genes-info'
import { GenomicVariants, ReportStatus } from 'domain/usecases/get-report'
import { route } from 'main/routes'
import { ButtonRound, FlexGrid, MutationAmplification, NotFound, Title } from 'presentation/components'
import ChartBar from 'presentation/components/chart-bar/chart-bar'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { useRecoilValue } from 'recoil'
import { reportAtom } from '../../report-atom'
import GenomicVariantsItem from './genomic-variants-item'
import './genomic-variants.scss'
import ModalCreateGenomicVariant from '../modal-create-genomic-variant/modal-create-genomic-variant'
import { ReportsService } from 'infra/services/reports-service'
import { add, star } from 'ionicons/icons'
import { UserInfo, UserRole } from 'infra/services/usecases/get-user-info'
import { InStatus } from 'presentation/components/in-status/in-status'

interface GenomicVariantsProps {
  reportsService: ReportsService
  onUpdateGenomicVariants: () => void
  user: UserInfo | null
}

const GenomicVariantList: React.FC<GenomicVariantsProps> = (props) => {
  const { report, isLoadingReport, genesInfo, isLoadingGenes } = useRecoilValue(reportAtom)
  const [genomicVariantsShow, setGenomicVariantsShow] = useState(report?.genomic_variants)
  const qtyToShow = 3
  const [showShowMore, setShowShowMore] = useState<null | boolean>(false)
  const hasGenomicVariants = report?.genomic_variants && report.genomic_variants.length
  const navigate = useHistory()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [genomicVariantToEdit, setGenomicVariantToEdit] = useState<GenomicVariants | null>(null)
  const [isDeleteGenVariantAlertOpen, setIsDeleteGenVariantAlertOpen] = useState(false)
  const [genVariantToDelete, setGenVariantToDelete] = useState<GenomicVariants | null>(null)

  useEffect(() => {
    setGenomicVariantsShow(report?.genomic_variants?.slice(0, qtyToShow))
    setShowShowMore(true)
  }, [report?.genomic_variants])

  const updateGenomicVariantList = () => {
    setGenomicVariantsShow(report?.genomic_variants?.slice(0, qtyToShow))
    setShowShowMore(true)
  }

  const isInReview = (report?.status === ReportStatus.processed || report?.status === ReportStatus.back_to_review) && (props.user?.role === UserRole.Oncologist || props.user?.role === UserRole.MedicalAssistant)

  const canView = (report?.status === ReportStatus.reviewed && (props.user?.role === UserRole.Oncologist || props.user?.role === UserRole.MedicalAssistant)) || (props.user?.role === UserRole.Reviewer && report?.status !== ReportStatus.processing)
  return (
    <section className="genomic-variants">
      {
        !isLoadingReport && (
          <FlexGrid justifyContent="space-between">
            <Title size="h2" className="gv-custom-title">
              Genomic Variants
            </Title>
            <FlexGrid gap={16}>
              <MutationAmplification />
            </FlexGrid>
          </FlexGrid>
        )
      }
      {
        isInReview && !isLoadingReport &&
        <InStatus>This report is in review</InStatus>
      }
      {report?.status === ReportStatus.processing && !isLoadingReport && (
        <div className="gv-processing">
          <NotFound>The report is being processed</NotFound>
        </div>
      )}
      {canView && (
        <>
          {isLoadingReport ? (
            <div className="gv-grid">
              <IonSkeletonText animated style={{ height: '130px', width: '31%' }} />
              <IonSkeletonText animated style={{ height: '130px', width: '31%' }} />
              <IonSkeletonText animated style={{ height: '130px', width: '31%' }} />
            </div>
          ) : (
            <>
              <div className="gv-grid">
                {genomicVariantsShow &&
                  genomicVariantsShow.map((variant, index) => (
                    <GenomicVariantsItem
                      editable={props.user?.role === UserRole.Reviewer}
                      item={variant}
                      key={index}
                      onDelete={() => {
                        setGenVariantToDelete(variant)
                        setIsDeleteGenVariantAlertOpen(true)
                      }}
                      onSearchLink={() => {
                        navigate.push(route.explore.url, {
                          geneName: variant.name,
                          cancerType: report?.patient.cancer_type || report?.diagnosis,
                        })
                      }}
                      onEdit={() => {
                        setGenomicVariantToEdit(variant)
                        setIsModalOpen(true)
                      }}
                      showFrquencyLabel={!!(genesInfo && genesInfo[variant.name] && genesInfo[variant.name].length)}
                      chart={
                        isLoadingGenes ? (
                          <FlexGrid justifyContent={'center'}>
                            <IonSpinner name="dots" />
                          </FlexGrid>
                        ) : genesInfo && genesInfo[variant.name] ? (
                          genesInfo[variant.name].map((chartItem, key) => (
                            <ChartBar key={key} textInTheBar={true} geneInfo={chartItem as GeneInfoItem} />
                          ))
                        ) : (
                          <></>
                        )
                      }
                    />
                  ))}
              </div>
            </>
          )}
          {
            report?.genomic_variants?.some((variant) => variant.suspected_germline) && (
              <div className="gv-legend">
                <IonIcon className="gi-star" icon={star} /> Suspected germline variant
              </div>
            )
          }

          {!genomicVariantsShow && !isLoadingReport && report?.status !== ReportStatus.processed && (
            <div className="gv-processing">
              <NotFound>No Genomic Variants available for the patient</NotFound>
            </div>
          )}
          {!isLoadingReport && (
            <div className="gv-actions">
              {((hasGenomicVariants as number) > qtyToShow && showShowMore !== false) &&
                <div className="gv-show-more">
                  <ButtonRound
                    onClick={() => {
                      setGenomicVariantsShow(report?.genomic_variants)
                      setShowShowMore(false)
                    }}
                    size="small"
                  >
                    <IonIcon icon={add} />
                    More Genomic Variants
                  </ButtonRound>
                </div>
              }
              {props.user?.role === UserRole.Reviewer && report?.status !== ReportStatus.processing && (
                <IonButton
                  fill="outline"
                  size="small"
                  className="gv-add-variant"
                  onClick={() => {
                    setGenomicVariantToEdit(null)
                    setIsModalOpen(true)
                  }}
                >
                  <IonIcon icon={add} />
                  Add Genomic Variant
                </IonButton>
              )}
            </div>
          )}
        </>
      )}

      <IonAlert
        isOpen={isDeleteGenVariantAlertOpen}
        onDidDismiss={() => setIsDeleteGenVariantAlertOpen(false)}
        header="Are you sure you want to delete this genomic variant?"
        subHeader="This action cannot be undone."
        buttons={[
          { text: 'Cancel', role: 'cancel', handler: () => setIsDeleteGenVariantAlertOpen(false) },
          {
            text: 'Delete', role: 'confirm', handler: () => {
              props.reportsService.deleteGenomicVariant({
                genomic_variant_id: genVariantToDelete?.id.toString() as string,
                report_id: report?.report_id as string,
              }).then(() => {
                updateGenomicVariantList()
                props.onUpdateGenomicVariants()
              })
            }
          },
        ]}
      ></IonAlert>
      {
        report && (
          <ModalCreateGenomicVariant
            reportId={report?.report_id}
            reportsService={props.reportsService}
            isOpen={isModalOpen}
            setIsOpen={setIsModalOpen}
            onDismiss={() => {
              setGenomicVariantToEdit(null)
            }}
            onDidDismiss={() => {
              updateGenomicVariantList()
              props.onUpdateGenomicVariants()
              setIsModalOpen(false)
            }}
            genomicVariant={genomicVariantToEdit}
          />
        )}
    </section>
  )
}

export default GenomicVariantList
